/**
 * Submit Ticket Drawer (Dumb Component)
 * No Redux — receives all data and callbacks via props
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation, useForm, zodResolver, z } from '@spcc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Textarea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  ImagePlus,
  X,
} from '@spcc/uikit'
import { CUSTOMER_CATEGORIES, INTERNAL_CATEGORIES } from '../../domain/constants'
import type { TicketChannel, TicketCategory, SourcePortal } from '../../domain/types'

const ticketSchema = z.object({
  subject: z.string().min(3, 'Subject must be at least 3 characters').max(255, 'Subject cannot exceed 255 characters'),
  description: z.string().min(10, 'Please provide more detail (at least 10 characters)'),
  category: z.string().min(1, 'Please select a category'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  contactEmail: z.string().email('Please enter a valid email').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
})

type TicketFormValues = z.infer<typeof ticketSchema>

export interface SubmitTicketFormData {
  subject: string
  description: string
  category: TicketCategory
  channel: TicketChannel
  priority: 'low' | 'medium' | 'high' | 'urgent'
  sourcePortal: SourcePortal
  contactEmail?: string
  contactPhone?: string
  attachmentFiles?: File[]
}

interface SubmitTicketDrawerProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: SubmitTicketFormData) => void
  loading?: boolean
  channel: TicketChannel
  sourcePortal: SourcePortal
  userEmail?: string | null
  userDisplayName?: string | null
  pickImageFiles?: () => Promise<File[]>
}

const MAX_FILES = 5
const MAX_FILE_SIZE_MB = 10

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function SubmitTicketDrawer({
  open,
  onClose,
  onSubmit,
  loading,
  channel,
  sourcePortal,
  userEmail,
  userDisplayName,
  pickImageFiles,
}: SubmitTicketDrawerProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([])
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [attachmentError, setAttachmentError] = useState<string | null>(null)

  const categories = channel === 'customer_to_spcc' ? CUSTOMER_CATEGORIES : INTERNAL_CATEGORIES

  const categoryLabels: Record<string, string> = {
    order_issue: t('support.categoryOrder', 'Order Issue'),
    delivery_issue: t('support.categoryDelivery', 'Delivery Issue'),
    payment_issue: t('support.categoryPayment', 'Payment Issue'),
    account_issue: t('support.categoryAccount', 'Account Issue'),
    platform_bug: t('support.categoryBug', 'Platform Bug'),
    feature_request: t('support.categoryFeature', 'Feature Request'),
    general: t('support.categoryGeneral', 'General'),
    other: t('support.categoryOther', 'Other'),
  }

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<TicketFormValues>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      priority: 'medium',
      category: '',
      subject: '',
      description: '',
      contactEmail: userEmail ?? '',
      contactPhone: '',
    },
  })

  useEffect(() => {
    if (open && userEmail) {
      setValue('contactEmail', userEmail)
    }
  }, [open, userEmail, setValue])

  // Clean up object URLs when drawer closes
  useEffect(() => {
    if (!open) {
      previewUrls.forEach((url) => URL.revokeObjectURL(url))
      setPreviewUrls([])
      setAttachmentFiles([])
      setAttachmentError(null)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  function addAttachmentFiles(selected: File[]) {
    if (!selected.length) return

    const oversized = selected.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024)
    if (oversized.length > 0) {
      setAttachmentError(t('support.fileTooLarge', `Files must be under ${MAX_FILE_SIZE_MB}MB`))
      return
    }

    const combined = [...attachmentFiles, ...selected].slice(0, MAX_FILES)
    const newUrls = combined.map((f) => URL.createObjectURL(f))

    previewUrls.forEach((url) => URL.revokeObjectURL(url))
    setPreviewUrls(newUrls)
    setAttachmentFiles(combined)
    setAttachmentError(null)
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    addAttachmentFiles(Array.from(e.target.files ?? []))

    // Reset file input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function handleAddPhotoClick() {
    if (!pickImageFiles) {
      fileInputRef.current?.click()
      return
    }

    addAttachmentFiles(await pickImageFiles())
  }

  function removeFile(index: number) {
    URL.revokeObjectURL(previewUrls[index])
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index))
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index))
  }

  const handleFormSubmit = (values: TicketFormValues) => {
    if (attachmentFiles.length === 0) {
      setAttachmentError(t('support.attachmentsRequired', 'At least one photo is required'))
      return
    }

    onSubmit({
      subject: values.subject,
      description: values.description,
      category: values.category as TicketCategory,
      channel,
      priority: values.priority,
      sourcePortal,
      contactEmail: values.contactEmail || userEmail || undefined,
      contactPhone: values.contactPhone || undefined,
      attachmentFiles,
    })
    reset()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <BaseDrawer
      open={open}
      onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}
      title={t('support.submitTicket', 'Submit a Ticket')}
      fixedWidth={500}
      headerContent={
        <h3 className="text-lg font-semibold text-white">{t('support.submitTicket', 'Submit a Ticket')}</h3>
      }
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4 p-4">
        {userDisplayName && (
          <div className="rounded-lg bg-muted/50 p-3 text-sm">
            <span className="text-muted-foreground">{t('support.submittingAs', 'Submitting as')}:</span>{' '}
            <span className="font-medium">{userDisplayName}</span>
            {userEmail && <span className="text-muted-foreground"> ({userEmail})</span>}
          </div>
        )}

        {/* Photo upload — mandatory */}
        <div className="space-y-2">
          <Label>
            {t('support.fieldAttachments', 'Photos')} <span className="text-destructive">*</span>
          </Label>
          <p className="text-xs text-muted-foreground">
            {t('support.attachmentsHint', `Upload up to ${MAX_FILES} photos (max ${MAX_FILE_SIZE_MB}MB each)`)}
          </p>

          {/* Preview grid */}
          {attachmentFiles.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {attachmentFiles.map((file, i) => (
                <div key={`${file.name}-${i}`} className="relative group rounded-lg overflow-hidden border bg-muted aspect-square">
                  <img
                    src={previewUrls[i]}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 end-1 p-0.5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={t('support.removePhoto', 'Remove photo')}
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <p className="absolute bottom-0 inset-x-0 px-1 py-0.5 text-[10px] text-white bg-black/50 truncate">
                    {formatBytes(file.size)}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Upload zone */}
          {attachmentFiles.length < MAX_FILES && (
            <button
              type="button"
              onClick={handleAddPhotoClick}
              className="w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border hover:border-primary/60 bg-muted/30 hover:bg-muted/50 transition-colors p-6 text-muted-foreground"
            >
              <ImagePlus className="w-8 h-8" />
              <span className="text-sm font-medium">{t('support.addPhoto', 'Click to add photos')}</span>
              <span className="text-xs">{t('support.addPhotoFormats', 'JPG, PNG, WEBP, GIF')}</span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />

          {attachmentError && (
            <p className="text-sm text-destructive">{attachmentError}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>{t('support.fieldSubject', 'Subject')} <span className="text-destructive">*</span></Label>
          <Input {...register('subject')} placeholder={t('support.subjectPlaceholder', 'Brief summary of your issue')} />
          {errors.subject && <p className="text-sm text-destructive">{errors.subject.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('support.fieldCategory', 'Category')} <span className="text-destructive">*</span></Label>
            <Select value={watch('category')} onValueChange={(v) => setValue('category', v, { shouldValidate: true })}>
              <SelectTrigger>
                <SelectValue placeholder={t('support.selectCategory', 'Select category')} />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {categoryLabels[cat] ?? cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && <p className="text-sm text-destructive">{errors.category.message}</p>}
          </div>

          <div className="space-y-2">
            <Label>{t('support.fieldPriority', 'Priority')}</Label>
            <Select value={watch('priority')} onValueChange={(v) => setValue('priority', v as any)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('support.priorityLow', 'Low')}</SelectItem>
                <SelectItem value="medium">{t('support.priorityMedium', 'Medium')}</SelectItem>
                <SelectItem value="high">{t('support.priorityHigh', 'High')}</SelectItem>
                <SelectItem value="urgent">{t('support.priorityUrgent', 'Urgent')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t('support.fieldDescription', 'Description')} <span className="text-destructive">*</span></Label>
          <Textarea
            {...register('description')}
            placeholder={t('support.descriptionPlaceholder', 'Describe your issue in detail...')}
            rows={5}
          />
          {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('support.fieldEmail', 'Contact Email')}</Label>
            <Input
              {...register('contactEmail')}
              type="email"
              placeholder={t('support.emailPlaceholder', 'email@example.com')}
              disabled={!!userEmail}
            />
            {errors.contactEmail && <p className="text-sm text-destructive">{errors.contactEmail.message}</p>}
          </div>

          <div className="space-y-2">
            <Label>{t('support.fieldPhone', 'Contact Phone')}</Label>
            <Input {...register('contactPhone')} placeholder={t('support.phonePlaceholder', '+966 5X XXX XXXX')} />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={handleClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? t('support.submitting', 'Submitting...') : t('support.submit', 'Submit Ticket')}
          </Button>
        </div>
      </form>
    </BaseDrawer>
  )
}

